import { Box, Flex } from "@chakra-ui/react";
import { Skeletons } from "pixelize-design-library";
import { brandColors, radii, spacing } from "@core/theme";

const Line = ({ width, height = "14px" }: { width: string; height?: string }) => (
  <Skeletons type="skeleton" width={width} height={height} />
);

export type SkeletonProductGridCardProps = {
  /** Matches `ProductGridCard` `density` — grids on home/shop use compact. */
  compact?: boolean;
};

// Mirrors ProductGridCard: image, title/subtitle/price, full-width cart button.
export const SkeletonProductGridCard = ({ compact = true }: SkeletonProductGridCardProps) => {
  const isCompact = compact;

  return (
    <Box
      borderWidth={1}
      borderColor={brandColors.border[300]}
      borderRadius={isCompact ? radii.sm : radii.md}
      overflow="hidden"
      bg="white"
      h={isCompact ? "100%" : undefined}
      display={isCompact ? "flex" : undefined}
      flexDirection={isCompact ? "column" : undefined}
      w="100%"
    >
      <Box aspectRatio={isCompact ? 1.35 : 1} w="100%" bg={brandColors.gray[100]} flexShrink={0} />
      <Box
        flex={isCompact ? 1 : undefined}
        display={isCompact ? "flex" : undefined}
        flexDirection="column"
        p={isCompact ? `${spacing[2]}px` : `${spacing[3]}px`}
      >
        <Flex direction="column" gap={isCompact ? 1 : 2} flex={isCompact ? 1 : undefined}>
          <Line width="95%" height={isCompact ? "11px" : "14px"} />
          <Line width="72%" height={isCompact ? "10px" : "12px"} />
          <Flex align="center" gap={1} wrap="wrap" mt={isCompact ? 0.5 : 1}>
            <Line width={isCompact ? "38%" : "42%"} height={isCompact ? "12px" : "14px"} />
            <Line width={isCompact ? "28%" : "30%"} height={isCompact ? "10px" : "12px"} />
          </Flex>
        </Flex>
        <Box mt={isCompact ? "auto" : 2} pt={isCompact ? 1.5 : 0}>
          <Skeletons
            type="skeleton"
            width="100%"
            height={isCompact ? "36px" : "44px"}
            bg={brandColors.gray[200]}
          />
        </Box>
      </Box>
    </Box>
  );
};

// Mirrors ProductCard / list rows: thumbnail + text lines.
export const SkeletonProductRow = () => (
  <Flex
    gap={{ base: 3, md: 4 }}
    p={{ base: 2, md: 3 }}
    borderWidth={1}
    borderColor={brandColors.border[300]}
    borderRadius={radii.md}
    w="100%"
  >
    <Box
      boxSize={{ base: "64px", md: "88px" }}
      borderRadius={radii.base}
      bg={brandColors.gray[100]}
      flexShrink={0}
    />
    <Flex direction="column" gap={2} flex={1} justify="center" minW={0}>
      <Line width="90%" />
      <Line width="60%" height="12px" />
      <Line width="30%" />
    </Flex>
  </Flex>
);

// Mirrors BrandTile.
export const SkeletonBrandTile = () => (
  <Flex
    justify="center"
    p={{ base: 3, md: 4 }}
    borderWidth={1}
    borderColor={brandColors.border[300]}
    borderRadius={radii.md}
    w="100%"
    minH={{ base: "48px", md: "56px" }}
  >
    <Line width="60%" height="16px" />
  </Flex>
);

export const SkeletonAddressCard = () => (
  <Box p={4} borderWidth={1} borderColor={brandColors.border[300]} borderRadius={radii.md} w="100%">
    <Flex direction="column" gap={2}>
      <Line width="40%" height="16px" />
      <Line width="55%" />
      <Line width="70%" />
      <Flex gap={2} justify="flex-end" mt={1}>
        <Line width="44px" height="20px" />
        <Line width="52px" height="20px" />
      </Flex>
    </Flex>
  </Box>
);

// Mirrors CategoryImageTile: 96px image + single title line.
export const SkeletonCategoryTile = () => (
  <Box
    p={3}
    borderWidth={1}
    borderColor={brandColors.border[300]}
    borderRadius={radii.md}
    w="100%"
    textAlign="center"
  >
    <Box
      h={{ base: "80px", sm: "96px" }}
      w="100%"
      borderRadius={radii.base}
      bg={brandColors.gray[100]}
    />
    <Box mt={2} mx="auto" maxW="90%">
      <Line width="100%" height="14px" />
    </Box>
  </Box>
);
